import { useCallback, useMemo, useState } from 'react';
import {
  BadgeCheck,
  Banknote,
  Calculator,
  Clock,
  HardHat,
} from 'lucide-react';
import DriverProfilesTab from './drivers/DriverProfilesTab';
import AttendanceTimesheetTab from './drivers/AttendanceTimesheetTab';
import SalaryStructureTab from './drivers/SalaryStructureTab';
import DriverPayroll from './drivers/DriverPayroll';
import DriverProfileModal from './drivers/DriverProfileModal';
import DriverDossierDrawer from './drivers/DriverDossierDrawer';
import {
  ATTENDANCE_PUNCHES,
  buildApiFeedEvent,
  buildDriverFromForm,
  deriveTimesheetsFromPunches,
  DRIVER_PROFILES,
  driverToModalForm,
  freshDriverModalForm,
  generateDriverId,
  MOBILE_API_FAILED_LABEL,
  MOBILE_API_SUCCESS_LABEL,
  SALARY_LEDGER,
  simulateCertDoc,
  simulateDriverPhoto,
  simulateIdentityDoc,
  TIMESHEET_ALLOWANCES,
} from './drivers/mockDriverErpData';

const DRIVER_TABS = [
  {
    id: 'profiles',
    label: 'Driver Profiles & Certifications',
    sublabel: 'Biometric vault · ADR · ADNOC',
    icon: BadgeCheck,
  },
  {
    id: 'attendance',
    label: 'Attendance & Timesheet Portal',
    sublabel: 'Mobile API feed · Geofence validation',
    icon: Clock,
  },
  {
    id: 'salary',
    label: 'Salary Structure Module',
    sublabel: 'Payroll ledger · Net salary calc',
    icon: Calculator,
  },
  {
    id: 'payroll',
    label: 'Driver Payroll Engine',
    sublabel: 'Contract calendar · Month-end PDF',
    icon: Banknote,
  },
];

export default function DriverManagement() {
  const [activeTab, setActiveTab] = useState('profiles');

  const [drivers, setDrivers] = useState(DRIVER_PROFILES);
  const [punches, setPunches] = useState(ATTENDANCE_PUNCHES);
  const [salaryLedger, setSalaryLedger] = useState(SALARY_LEDGER);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [modalForm, setModalForm] = useState(freshDriverModalForm);
  const [profileSavedMsg, setProfileSavedMsg] = useState('');
  const [viewingDriver, setViewingDriver] = useState(null);

  const [punchDriverId, setPunchDriverId] = useState(DRIVER_PROFILES[0]?.id || '');
  const [gpsFeedSourceId, setGpsFeedSourceId] = useState('jebel-ali');
  const [customLat, setCustomLat] = useState('24.9800');
  const [customLng, setCustomLng] = useState('55.0800');
  const [apiNotice, setApiNotice] = useState(null);

  const timesheets = useMemo(
    () => deriveTimesheetsFromPunches(punches, TIMESHEET_ALLOWANCES),
    [punches],
  );

  const [salaryDriverId, setSalaryDriverId] = useState(SALARY_LEDGER[0]?.driverId || '');
  const [basicSalary, setBasicSalary] = useState(SALARY_LEDGER[0]?.basicSalary || 0);
  const [tripIncentives, setTripIncentives] = useState(SALARY_LEDGER[0]?.tripIncentives || 0);
  const [overtime, setOvertime] = useState(SALARY_LEDGER[0]?.overtime || 0);
  const [penalties, setPenalties] = useState(SALARY_LEDGER[0]?.penalties || 0);

  const driverIdForUploads = editingDriver?.id || 'draft';

  const openAddModal = () => {
    setEditingDriver(null);
    setModalForm(freshDriverModalForm());
    setIsModalOpen(true);
  };

  const openEditModal = (driver) => {
    setEditingDriver(driver);
    setModalForm(driverToModalForm(driver));
    setIsModalOpen(true);
  };

  const openDossier = (driver) => {
    setViewingDriver(driver);
  };

  const closeDossier = () => {
    setViewingDriver(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingDriver(null);
    setModalForm(freshDriverModalForm());
  };

  const handleFieldChange = (field, value) => {
    setModalForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggleCert = (certKey) => {
    setModalForm((prev) => {
      const exists = prev.selectedCerts.includes(certKey);
      const selectedCerts = exists
        ? prev.selectedCerts.filter((k) => k !== certKey)
        : [...prev.selectedCerts, certKey];
      const certAttachments = { ...prev.certAttachments };
      if (exists) {
        delete certAttachments[certKey];
      }
      return { ...prev, selectedCerts, certAttachments };
    });
  };

  const handleProfilePhotoUpload = (file) => {
    setModalForm((prev) => ({
      ...prev,
      profilePhoto: simulateDriverPhoto(file, driverIdForUploads),
    }));
  };

  const handleIdentityDocUpload = (docKey, file) => {
    setModalForm((prev) => ({
      ...prev,
      identityVault: {
        ...prev.identityVault,
        [docKey]: simulateIdentityDoc(file, driverIdForUploads, docKey),
      },
    }));
  };

  const handleCertDocUpload = (certKey, file) => {
    setModalForm((prev) => ({
      ...prev,
      certAttachments: {
        ...prev.certAttachments,
        [certKey]: simulateCertDoc(file, driverIdForUploads, certKey),
      },
    }));
  };

  const handleContractFieldChange = (field, value) => {
    setModalForm((prev) => ({
      ...prev,
      contractDetails: {
        ...prev.contractDetails,
        [field]: value,
      },
    }));
  };

  const handleAddSpecialAllowance = () => {
    setModalForm((prev) => ({
      ...prev,
      contractDetails: {
        ...prev.contractDetails,
        specialAllowances: [
          ...(prev.contractDetails?.specialAllowances || []),
          { type: 'ADR/Hazmat Bonus', amount: 0 },
        ],
      },
    }));
  };

  const handleRemoveSpecialAllowance = (index) => {
    setModalForm((prev) => ({
      ...prev,
      contractDetails: {
        ...prev.contractDetails,
        specialAllowances: (prev.contractDetails?.specialAllowances || []).filter(
          (_, i) => i !== index,
        ),
      },
    }));
  };

  const handleUpdateSpecialAllowance = (index, field, value) => {
    setModalForm((prev) => ({
      ...prev,
      contractDetails: {
        ...prev.contractDetails,
        specialAllowances: (prev.contractDetails?.specialAllowances || []).map((item, i) =>
          i === index ? { ...item, [field]: value } : item,
        ),
      },
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!modalForm.name.trim()) return;

    const contractSalary = Number(modalForm.contractDetails?.basicSalary);
    if (!contractSalary || contractSalary <= 0) return;
    if (!modalForm.contractDetails?.foodAllowanceType) return;
    if (!modalForm.contractDetails?.overtimeRule) return;

    if (editingDriver) {
      const updated = buildDriverFromForm(
        modalForm,
        editingDriver.id,
        editingDriver.certifications,
      );

      setDrivers((prev) =>
        prev.map((d) => (d.id === editingDriver.id ? updated : d)),
      );

      setSalaryLedger((prev) =>
        prev.map((r) =>
          r.driverId === editingDriver.id
            ? { ...r, driverName: updated.name, basicSalary: updated.contractDetails.basicSalary }
            : r,
        ),
      );

      if (salaryDriverId === editingDriver.id) {
        setBasicSalary(updated.contractDetails.basicSalary);
      }

      if (viewingDriver?.id === editingDriver.id) {
        setViewingDriver(updated);
      }

      setProfileSavedMsg(`${updated.name} profile updated.`);
    } else {
      const newId = generateDriverId(drivers);
      const newDriver = buildDriverFromForm(modalForm, newId);

      setDrivers((prev) => [newDriver, ...prev]);
      setSalaryLedger((prev) => [
        {
          driverId: newId,
          driverName: newDriver.name,
          basicSalary: newDriver.contractDetails.basicSalary,
          tripIncentives: 500,
          overtime: 0,
          nightAllowance: 0,
          fuelSavingBonus: 100,
          safeDrivingBonus: 200,
          penalties: 0,
        },
        ...prev,
      ]);

      if (!punchDriverId) setPunchDriverId(newId);
      setProfileSavedMsg(`${newDriver.name} added to driver registry.`);
    }

    closeModal();
    setTimeout(() => setProfileSavedMsg(''), 3000);
  };

  const handleSimulateApiPunch = (punchType) => {
    const driver = drivers.find((d) => d.id === punchDriverId);
    if (!driver) return;

    const event = buildApiFeedEvent({
      driverId: driver.id,
      driverName: driver.name,
      punchType,
      gpsFeedSourceId,
      customLat,
      customLng,
    });

    setPunches((prev) => [event, ...prev]);

    if (event.geofenceStatus === 'failed') {
      setApiNotice({
        type: 'failed',
        message: `❌ ${MOBILE_API_FAILED_LABEL} · ${event.latitude?.toFixed(4)}, ${event.longitude?.toFixed(4)}`,
      });
    } else {
      setApiNotice({
        type: 'success',
        message: `🟢 ${MOBILE_API_SUCCESS_LABEL} · ${event.location} (${event.latitude?.toFixed(4)}, ${event.longitude?.toFixed(4)})`,
      });
    }
  };

  const loadSalaryFields = useCallback((driverId) => {
    const record = salaryLedger.find((r) => r.driverId === driverId);
    if (!record) return;
    setBasicSalary(record.basicSalary);
    setTripIncentives(record.tripIncentives);
    setOvertime(record.overtime);
    setPenalties(record.penalties);
  }, [salaryLedger]);

  const handleSalaryDriverChange = (driverId) => {
    setSalaryDriverId(driverId);
    loadSalaryFields(driverId);
  };

  const handleUpdateLedger = useCallback((driverId, fields) => {
    setSalaryLedger((prev) =>
      prev.map((r) => (r.driverId === driverId ? { ...r, ...fields } : r)),
    );
  }, []);

  return (
    <section aria-label="Driver management" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-logi-grid pb-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-logi-accent/10 ring-1 ring-logi-accent/30">
              <HardHat className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-logi-accent/80">
              OGTS LogiCore™ · Human Capital Module
            </span>
          </div>
          <h2 className="text-xl font-bold tracking-tight text-logi-text sm:text-2xl">
            Driver Management
          </h2>
          <p className="mt-1 text-sm text-logi-muted">
            Biometric vault · Identity documents · Certification clearing dashboard
          </p>
        </div>
        <div className="rounded-xl border border-logi-accent/30 bg-logi-accent/10 px-3 py-2 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
            Active Drivers
          </p>
          <p className="font-mono text-lg font-bold text-logi-accent">{drivers.length}</p>
        </div>
      </div>

      <div className="flex flex-col gap-1 rounded-xl border border-logi-grid bg-white p-1 sm:flex-row">
        {DRIVER_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-1 items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-all duration-200 ${
                isActive
                  ? 'bg-logi-accent/10 text-logi-accent ring-1 ring-logi-accent/30'
                  : 'text-logi-muted hover:bg-logi-silver/80 hover:text-logi-text'
              }`}
            >
              <Icon
                className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-logi-accent' : 'text-logi-text0'}`}
                strokeWidth={1.75}
              />
              <div className="min-w-0">
                <p className="truncate text-xs font-semibold sm:text-sm">{tab.label}</p>
                <p className="hidden truncate text-[10px] text-logi-text0 sm:block">
                  {tab.sublabel}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      <div key={activeTab} className="animate-[fadeSlideIn_0.25s_ease-out]">
        {activeTab === 'profiles' && (
          <DriverProfilesTab
            drivers={drivers}
            onAddDriver={openAddModal}
            onEditDriver={openEditModal}
            onViewDossier={openDossier}
            savedMsg={profileSavedMsg}
          />
        )}
        {activeTab === 'attendance' && (
          <AttendanceTimesheetTab
            feedEvents={punches}
            timesheets={timesheets}
            drivers={drivers}
            selectedDriverId={punchDriverId}
            onDriverChange={setPunchDriverId}
            gpsFeedSourceId={gpsFeedSourceId}
            onGpsFeedSourceChange={setGpsFeedSourceId}
            customLat={customLat}
            customLng={customLng}
            onCustomLatChange={setCustomLat}
            onCustomLngChange={setCustomLng}
            apiNotice={apiNotice}
            onSimulatePunchIn={() => handleSimulateApiPunch('PUNCH_IN')}
            onSimulatePunchOut={() => handleSimulateApiPunch('PUNCH_OUT')}
          />
        )}
        {activeTab === 'salary' && (
          <SalaryStructureTab
            ledger={salaryLedger}
            selectedDriverId={salaryDriverId}
            onDriverChange={handleSalaryDriverChange}
            basicSalary={basicSalary}
            onBasicSalaryChange={setBasicSalary}
            tripIncentives={tripIncentives}
            onTripIncentivesChange={setTripIncentives}
            overtime={overtime}
            onOvertimeChange={setOvertime}
            penalties={penalties}
            onPenaltiesChange={setPenalties}
            onUpdateLedger={handleUpdateLedger}
          />
        )}
        {activeTab === 'payroll' && <DriverPayroll />}
      </div>

      <DriverProfileModal
        isOpen={isModalOpen}
        isEditing={Boolean(editingDriver)}
        form={modalForm}
        onFieldChange={handleFieldChange}
        onToggleCert={handleToggleCert}
        onProfilePhotoUpload={handleProfilePhotoUpload}
        onIdentityDocUpload={handleIdentityDocUpload}
        onCertDocUpload={handleCertDocUpload}
        onContractFieldChange={handleContractFieldChange}
        onAddSpecialAllowance={handleAddSpecialAllowance}
        onRemoveSpecialAllowance={handleRemoveSpecialAllowance}
        onUpdateSpecialAllowance={handleUpdateSpecialAllowance}
        onSave={handleSave}
        onClose={closeModal}
      />

      <DriverDossierDrawer
        driver={viewingDriver}
        isOpen={Boolean(viewingDriver)}
        onClose={closeDossier}
      />
    </section>
  );
}
